import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { LoadingButton } from '@mui/lab';
import { Autocomplete, Button, Checkbox, Grid, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import OberonDialog from '~components/OberonDialog';
import { PublicHolidays } from '~hooks/usePublicHoliday/domain';

type UpdatePublicHolidayModalProps = {
  open: Boolean;
  publicHoliday?: PublicHolidays;
  timezoneList: string[];
  onClose: () => void;
  onAccept: (oldDate: string, oldName: string, data: PublicHolidays) => Promise<void>;
};

interface PublicHolidayForm {
  date: Date | null;
  name: string;
  information: string;
  timezones: string[];
  postcodes?: string[];
}

function UpdatePublicHolidayModal({
  publicHoliday,
  open,
  onAccept,
  onClose,
  timezoneList,
}: UpdatePublicHolidayModalProps) {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const {
    formState: { errors },
    handleSubmit,
    control,
    reset,
    watch,
  } = useForm<PublicHolidayForm>({
    mode: 'all',
    reValidateMode: 'onChange',
  });
  const timezones = watch('timezones');

  useEffect(() => {
    if (open && publicHoliday) {
      reset({
        date: new Date(publicHoliday.year, publicHoliday.month - 1, publicHoliday.day),
        name: publicHoliday.name,
        information: publicHoliday.information || '',
        timezones: publicHoliday.timezones,
        postcodes: publicHoliday.postcodes,
      });
    }

    return function cleanupUpdatePublicHolidayModal() {
      reset({ date: null, name: '', information: '', timezones: [], postcodes: [] });
    };
  }, [open, publicHoliday]);

  const onSubmit = handleSubmit(async (data: PublicHolidayForm) => {
    (document.activeElement as HTMLElement)?.blur();
    setSubmitting(true);
    try {
      const pad = (n: number) => String(n).padStart(2, '0');
      const oldDate = `${publicHoliday!.year}-${pad(publicHoliday!.month)}-${pad(publicHoliday!.day)}`;
      const oldName = publicHoliday!.name;

      const date = new Date(data.date as any);
      const holiday: PublicHolidays = {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
        name: data.name,
        information: data.information,
        timezones: data.timezones,
        postcodes: data.timezones.length === 1 ? data.postcodes : [],
      };
      await onAccept(oldDate, oldName, holiday);
    } catch (e) {
      return;
    } finally {
      setSubmitting(false);
    }
    reset();
  });

  return (
    <OberonDialog
      title='Edit Public Holiday'
      open={open && Boolean(publicHoliday)}
      onSubmit={onSubmit}
      content={
        <Grid container spacing={2}>
          <Grid size={12}>
            <Controller
              name='name'
              control={control}
              rules={{ required: 'Name is required.' }}
              render={({ field }) => (
                <TextField
                  fullWidth
                  variant='outlined'
                  label='Name'
                  disabled={submitting}
                  required={true}
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message}
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name='timezones'
              control={control}
              defaultValue={publicHoliday?.timezones || []}
              rules={{
                validate: (value) => (value && value.length > 0) || 'At least one timezone is required.',
              }}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  disableClearable
                  value={field.value || []}
                  options={timezoneList}
                  disableCloseOnSelect
                  onChange={(_, value) => field.onChange(value)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                        checkedIcon={<CheckBoxIcon fontSize='small' />}
                        checked={selected}
                        style={{ marginRight: 8 }}
                      />
                      {option}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Timezones'
                      placeholder='Select timezones'
                      required
                      error={Boolean(errors.timezones)}
                      helperText={errors.timezones?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name='postcodes'
              control={control}
              rules={{
                validate: (value) => {
                  if (!value) return true;
                  const str = Array.isArray(value) ? value.join(',') : String(value);
                  const tokens = str
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean);
                  if (tokens.length === 0) return true;
                  const invalid = tokens.find((t) => !/^\d{4}$/.test(t));
                  if (invalid) return `"${invalid}" is not a valid 4-digit postcode`;
                  const dupes = tokens.filter((t, i) => tokens.indexOf(t) !== i);
                  if (dupes.length > 0) return `Duplicate postcode: ${dupes[0]}`;
                  return true;
                },
              }}
              render={({ field }) => {
                const postcodesDisabled = !timezones || timezones.length !== 1;
                return (
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    variant='outlined'
                    label='Postcode'
                    disabled={submitting || postcodesDisabled}
                    helperText={
                      errors.postcodes?.message ??
                      (postcodesDisabled ? 'To enter postcodes, select only one timezone.' : undefined)
                    }
                    error={Boolean(errors.postcodes)}
                    {...field}
                  />
                );
              }}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name='date'
              control={control}
              rules={{
                required: 'Date is required.',
                validate: (value) => {
                  if (!value) return 'Date is required.';
                  const date = new Date(value);
                  if (isNaN(date.getTime())) return 'Invalid date.';
                  return true;
                },
              }}
              render={({ field }) => (
                <DatePicker
                  label='Date'
                  format='dd/MM/yyyy'
                  disabled={submitting}
                  value={field.value ? DateTime.fromJSDate(field.value) : null}
                  onChange={(newValue) => field.onChange(newValue ? newValue.toJSDate() : null)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: Boolean(errors.date),
                      helperText: errors.date?.message,
                    },
                  }}
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name='information'
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant='outlined'
                  label='Information'
                  disabled={submitting}
                  error={Boolean(errors.information)}
                  {...field}
                />
              )}
            />
          </Grid>
        </Grid>
      }
      actionFooter={
        <>
          <Button
            variant='text'
            disabled={submitting}
            onClick={() => {
              onClose();
            }}>
            Close
          </Button>

          <LoadingButton
            type='submit'
            variant='contained'
            disableElevation
            color='primary'
            disabled={submitting}
            loading={submitting}>
            Update
          </LoadingButton>
        </>
      }
    />
  );
}

export default UpdatePublicHolidayModal;
